import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { Mail } from "lucide-react";
import { formatFixedPrice, FREE_SHIPPING_THRESHOLDS } from "@/lib/fixedPrices";
import logo from "@/assets/logo.png";

export function Footer() {
  const { t, currency } = useLanguage();
  const displayCurrency = currency || 'USD';
  const threshold = FREE_SHIPPING_THRESHOLDS[displayCurrency] || FREE_SHIPPING_THRESHOLDS.USD;
  const formattedThreshold = formatFixedPrice(threshold, displayCurrency);

  return (
    <footer className="pt-12 relative" style={{ background: 'linear-gradient(180deg, hsl(var(--section-from)) 0%, hsl(var(--section-from)) 100%)' }} role="contentinfo" aria-label="Site footer">
      {/* Trust badges */}
      <div className="border-b border-border/50">
        <div className="container py-6">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
              {t('footer.freeShipping', { amount: formattedThreshold })}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
              {t('footer.securePayment')}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
              {t('footer.support')}
            </span>
          </div>
        </div>
      </div>

      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={logo} alt="PDRN SKIN logo" className="w-24 h-24 object-contain" />
              <span className="text-xl font-bold text-foreground">PDRN SKIN</span>
            </Link>
            <p className="text-muted-foreground text-sm max-w-md leading-relaxed">
              {t('footer.description')}
            </p>
            
            {/* Social links — hidden until real URLs are available
            <div className="flex gap-4 mt-6" aria-label="Social media links">
              <a href="#" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" aria-hidden="true" />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" aria-hidden="true" />
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" aria-label="Email">
                <Mail className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
            */}
          </div>

          {/* Quick Links */}
          <nav aria-label="Product links">
            <h4 className="font-semibold mb-4 text-foreground">{t('nav.shop')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/product/pdrn-pink-sheet-masks-10-pack" className="hover:text-primary transition-colors">{t('footer.sheetMasks')}</Link></li>
              <li><Link to="/product/pink-glow-serum-with-pdrn-peptides" className="hover:text-primary transition-colors">{t('footer.serum')}</Link></li>
              <li><Link to="/product/smart-capsule-cream-with-collagen" className="hover:text-primary transition-colors">{t('footer.cream')}</Link></li>
              <li><Link to="/product/complete-pdrn-trio-bundle" className="hover:text-primary transition-colors">{t('footer.bundle')}</Link></li>
            </ul>
          </nav>

          {/* Support */}
          <nav aria-label="Support links">
            <h4 className="font-semibold mb-4 text-foreground">{t('nav.contact')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><a href="/#faq" className="hover:text-primary transition-colors">{t('footer.faq')}</a></li>
              <li><Link to="/shipping" className="hover:text-primary transition-colors">{t('footer.shippingReturns')}</Link></li>
              <li><Link to="/returns" className="hover:text-primary transition-colors">{t('footer.returnPolicy')}</Link></li>
              <li><Link to="/privacy" className="hover:text-primary transition-colors">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors">{t('footer.termsOfService')}</Link></li>
              <li><Link to="/blog" className="hover:text-primary transition-colors">{t('nav.blog')}</Link></li>
              
            </ul>
          </nav>
        </div>
      </div>

      {/* Contact Support */}
      <div className="border-t border-border/50">
        <div className="container py-6">
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground text-center">
            <Mail className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
            <p>
              {t('footer.contactSupport')}{' '}
              <a href="mailto:info@pdrn-skin.com" className="text-primary hover:underline font-medium">
                info@pdrn-skin.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-border/50">
        <div className="container py-6 text-center text-sm text-muted-foreground">
          <p>{t('footer.copyright')}</p>
          <p className="mt-2 text-[10px] text-muted-foreground/40">
            IP Geolocation by <a href="https://www.iplocate.io" target="_blank" rel="noopener noreferrer" className="hover:text-muted-foreground/60 transition-colors">IPLocate.io</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
